import { z } from 'zod';

/**
 * How a configuration field is typed, validated, and rendered.
 *
 * The admin form for a provider is **generated** from these descriptors (W9) —
 * a new provider needs zero UI code — so the type is a contract between the
 * catalog and the form generator, not a hint.
 */
export const INTEGRATION_FIELD_TYPES = [
  'text',
  'password',
  'url',
  'number',
  'boolean',
  'email',
  /**
   * A **curated list with an escape** — never a closed enumeration.
   *
   * `options` is what the control offers; it is deliberately *not* what the
   * value schema enforces, because a vendor catalogue shrinks. See
   * `integrationFieldValueSchema` for why an enum here is an outage.
   *
   * A genuinely closed list — one whose members are a *platform* invariant
   * rather than a vendor's — would be a different type (`'enum'`), added when
   * something actually needs it. Do not turn this one into it.
   */
  'select',
  /**
   * A structured **document** carried as a string — a service-account file, not
   * a line of configuration.
   *
   * It exists because a descriptor otherwise says nothing about *length*: FCM's
   * ~2 KB service account was `text`, so the generated form gave it a one-line
   * input where a truncated paste is undetectable until push delivery fails in
   * production. Inferring long-form from the key name would be the same defect
   * `DurationFieldDef` records — a rename silently changing a control.
   *
   * A `json` field **may** be secret (unlike `select`): masking it in a *list*
   * is impossible, but a document editor that opens blank leaks nothing.
   */
  'json',
] as const;

export const IntegrationFieldTypeSchema = z.enum(INTEGRATION_FIELD_TYPES);

export type IntegrationFieldType = (typeof INTEGRATION_FIELD_TYPES)[number];

/** Field keys are lower camel case: they are object keys in a write payload. */
const FIELD_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

/** Env variable names are SCREAMING_SNAKE_CASE — the shape `process.env` carries. */
const ENV_VAR_PATTERN = /^[A-Z][A-Z0-9_]*$/;

/** An option value is a vendor identifier (`claude-opus-5`, `gemini-2.5-pro`), never prose. */
const OPTION_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;

/**
 * The ceiling on a `select` value: long enough for any vendor id, short enough
 * that a paste of something else is refused rather than stored.
 */
const SELECT_VALUE_MAX = 200;

/**
 * One configurable field of one provider (N21).
 *
 * `secret: true` → AES-256-GCM-encrypted at rest, returned only as `last4`,
 * never logged, never in audit before/after. `secret: false` → stored in
 * plaintext and shown/editable (bucket, endpoint, host…).
 *
 * `envVar` is the **fallback** name the vault's resolver reads when no value is
 * stored — read by that resolver alone, never by a consumer (U14/N21). It
 * exists so a fresh deployment works before staff have configured anything.
 */
export const IntegrationFieldDescriptorSchema = z
  .object({
    key: z
      .string()
      .regex(FIELD_KEY_PATTERN, 'A field key is lower camel case, e.g. "accessKeyId".'),
    type: IntegrationFieldTypeSchema,
    secret: z.boolean(),
    required: z.boolean(),
    envVar: z
      .string()
      .regex(ENV_VAR_PATTERN, 'An env variable name is SCREAMING_SNAKE_CASE.')
      .optional(),
    // `.readonly()` because a descriptor is a declaration: the catalog derives
    // this list from the model catalogue and nothing may splice into it.
    options: z
      .array(z.string().regex(OPTION_PATTERN, 'An option is an identifier.'))
      .readonly()
      .optional(),
    // The range a `number` may take (P30b1) — applied on the write AND when a
    // stored or env value is read back, like every other part of this schema.
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
  })
  .strict()
  .refine((field) => field.type !== 'password' || field.secret, {
    // A password-typed field that is not secret would be masked in the form and
    // stored in plaintext — the worst of both, and invisible to review.
    message: 'A "password" field must be secret.',
    path: ['secret'],
  })
  .superRefine((field, ctx) => {
    if (field.type !== 'number' && (field.min !== undefined || field.max !== undefined)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Only a "number" field carries bounds.',
        path: ['min'],
      });
    }

    if (field.min !== undefined && field.max !== undefined && field.min > field.max) {
      ctx.addIssue({
        code: 'custom',
        message: 'A floor cannot sit above its ceiling.',
        path: ['min'],
      });
    }
  })
  .superRefine((field, ctx) => {
    if (field.type !== 'select') {
      if (field.options !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'Only a "select" field carries options.',
          path: ['options'],
        });
      }

      return;
    }

    if (field.options === undefined || field.options.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'A "select" field declares at least one option.',
        path: ['options'],
      });

      return;
    }

    if (new Set(field.options).size !== field.options.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'Option values are unique.',
        path: ['options'],
      });
    }

    if (field.secret) {
      // A secret's stored value is never returned (N2/N21) — only its `last4` —
      // so a control offering a list of them could never show which is chosen.
      ctx.addIssue({
        code: 'custom',
        message: 'A "select" field is never secret.',
        path: ['secret'],
      });
    }
  });

export type IntegrationFieldDescriptor = z.infer<typeof IntegrationFieldDescriptorSchema>;

/**
 * The schema **one field's value** must satisfy — the single place a field type
 * turns into validation (U3), used by the api when a write lands and by the
 * admin form before it is sent.
 *
 * An empty string is never a value: a blank secret **keeps** the stored one and
 * a blank non-secret **clears** the field (falling back to env), so both are
 * handled as a `null` before validation ever runs.
 *
 * **A `select` is a plain bounded string, never `z.enum(options)`** — and the
 * reason is the read path, not the write one. This schema also runs when a
 * stored value is *read back* (`IntegrationResolutionService.fromVault`, whose
 * failure is a thrown `CorruptIntegrationValueError`) and when the env fallback
 * is coerced (whose failure silently reads as *missing*). An enum would
 * therefore make a shrinking vendor catalogue an outage on a deploy that
 * changed no data: every AI call and the integrations overview 500, the
 * operator cannot even edit the API key to repair it because the form validates
 * the whole object, and a correct `AI_TEXT_MODEL` naming a newer model resolves
 * to nothing. Membership is reported instead — see `isCustomFieldValue`.
 */
export function integrationFieldValueSchema(
  field: IntegrationFieldDescriptor,
): z.ZodType<string | number | boolean> {
  switch (field.type) {
    case 'number': {
      let schema = z.number().finite();
      if (field.min !== undefined) schema = schema.min(field.min);
      if (field.max !== undefined) schema = schema.max(field.max);
      return schema;
    }
    case 'boolean':
      return z.boolean();
    case 'url':
      return z.url();
    case 'email':
      return z.email();
    case 'select':
      return z.string().min(1).max(SELECT_VALUE_MAX);
    // A `json` value is **NOT** parse-checked, for the reason the doc block
    // above gives for `select`: this schema runs on every *read* too. A
    // base64-wrapped service account is a real FCM convention, so requiring
    // `JSON.parse` would make a working deployment resolve as *missing* — an
    // outage on a deploy that changed no data. The editor that authors the
    // value validates it; the contract only carries it.
    case 'json':
    case 'password':
    case 'text':
      return z.string().min(1).max(8192);
  }
}

/**
 * Whether a value sits outside the field's curated list — the honest half of
 * what an enum would have enforced.
 *
 * It is a **fact to report**, never a verdict: the config form notes it beside
 * the control so an operator knows they are off-catalogue, and the panel and
 * the pricing table use the same answer. `false` for every field that carries
 * no list, so a caller never has to check the type first.
 */
export function isCustomFieldValue(
  field: IntegrationFieldDescriptor,
  value: string | number | boolean,
): boolean {
  if (field.options === undefined || typeof value !== 'string') {
    return false;
  }

  return !field.options.includes(value);
}
